import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Car, Settings, Trash2, Eye } from 'lucide-react';
import { api } from '../../lib/api';
import { useUIStore } from '../../store/ui';

interface Vehicle {
  id: string;
  nickname: string;
  make: string;
  model: string;
  year: number;
  tagline?: string;
  status: string;
  featuredImageUrl?: string;
}

export function GaragePage() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { addToast } = useUIStore();

  // Form state for new vehicle
  const [formData, setFormData] = useState({
    nickname: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    tagline: '',
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchVehicles();
  }, []);

  async function fetchVehicles() {
    try {
      const res = await api.get('/api/users/me/vehicles');
      if (res.vehicles) {
        setVehicles(res.vehicles);
      }
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateVehicle(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);

    try {
      const res = await api.post('/api/users/me/vehicles', formData);
      if (res.vehicle) {
        setVehicles([...vehicles, res.vehicle]);
        setShowCreateModal(false);
        setFormData({
          nickname: '',
          make: '',
          model: '',
          year: new Date().getFullYear(),
          tagline: '',
        });
        addToast('Vehicle added to your garage!', 'success');
      }
    } catch (error) {
      addToast('Failed to create vehicle', 'error');
    } finally {
      setCreating(false);
    }
  }

  async function handleDeleteVehicle(id: string) {
    if (!confirm('Are you sure you want to remove this vehicle from your garage?')) {
      return;
    }

    try {
      await api.delete(`/api/users/me/vehicles/${id}`);
      setVehicles(vehicles.filter((v) => v.id !== id));
      addToast('Vehicle removed from garage', 'success');
    } catch (error) {
      addToast('Failed to delete vehicle', 'error');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Garage</h1>
          <p className="text-gray-600 mt-1">
            Manage your vehicles and showcase your builds.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary"
        >
          <Plus size={18} className="mr-2" />
          Add Vehicle
        </button>
      </div>

      {/* Vehicles Grid */}
      {vehicles.length === 0 ? (
        <div className="card p-12 text-center">
          <Car className="mx-auto text-gray-400 mb-4" size={48} />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Your garage is empty
          </h2>
          <p className="text-gray-600 mb-6">
            Add your first vehicle to start building your driver profile.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            <Plus size={18} className="mr-2" />
            Add Your First Vehicle
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vehicles.map((vehicle) => (
            <div key={vehicle.id} className="card overflow-hidden">
              {/* Image */}
              <div className="aspect-video bg-gray-200 flex items-center justify-center">
                {vehicle.featuredImageUrl ? (
                  <img
                    src={vehicle.featuredImageUrl}
                    alt={vehicle.nickname}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Car className="text-gray-400" size={48} />
                )}
              </div>

              {/* Details */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 text-lg">
                  {vehicle.nickname}
                </h3>
                <p className="text-gray-600">
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </p>
                {vehicle.tagline && (
                  <p className="text-sm text-gray-500 mt-1 italic">
                    "{vehicle.tagline}"
                  </p>
                )}

                {/* Actions */}
                <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-gray-200">
                  <Link
                    to={`/vehicle/${vehicle.id}`}
                    className="btn btn-sm btn-outline flex-1"
                  >
                    <Eye size={14} className="mr-1" />
                    View
                  </Link>
                  <button className="btn btn-sm btn-outline">
                    <Settings size={14} />
                  </button>
                  <button
                    onClick={() => handleDeleteVehicle(vehicle.id)}
                    className="btn btn-sm btn-outline text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Add New Vehicle</h2>
            </div>

            <form onSubmit={handleCreateVehicle} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nickname *
                </label>
                <input
                  type="text"
                  required
                  value={formData.nickname}
                  onChange={(e) =>
                    setFormData({ ...formData, nickname: e.target.value })
                  }
                  className="input w-full"
                  placeholder="e.g., The Beast, Project X"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Make *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.make}
                    onChange={(e) =>
                      setFormData({ ...formData, make: e.target.value })
                    }
                    className="input w-full"
                    placeholder="e.g., Ford"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.model}
                    onChange={(e) =>
                      setFormData({ ...formData, model: e.target.value })
                    }
                    className="input w-full"
                    placeholder="e.g., Mustang"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year *
                </label>
                <input
                  type="number"
                  required
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  value={formData.year}
                  onChange={(e) =>
                    setFormData({ ...formData, year: parseInt(e.target.value) })
                  }
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tagline
                </label>
                <input
                  type="text"
                  value={formData.tagline}
                  onChange={(e) =>
                    setFormData({ ...formData, tagline: e.target.value })
                  }
                  className="input w-full"
                  placeholder="e.g., Built not bought"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-outline flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="btn btn-primary flex-1"
                >
                  {creating ? 'Adding...' : 'Add Vehicle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
